import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  ColumnComponent,
  ColumnDefinition,
  Options,
  RowComponent,
  TabulatorFull,
} from "tabulator-tables";

export type TableEventsV2 = {
  headerSort?: (value: { field: string; direction: "desc" | "asc" }) => void;
  rowClick?: (e: any, row: RowComponent) => void;
  rowSelected?: (row: RowComponent) => void;
  rowDeselected?: (row: RowComponent) => void;
  dataLoaded?: (data: any) => void;
  rowSelectionChanged?: (
    data: any,
    rows: any,
    selected: any,
    deselected: any
  ) => void;
  menuHeaderClick?: (e: any, col: ColumnComponent, actionTag: string) => void;
  onTableReady?: () => void;
  onRowDbClick?: (e: any, row: RowComponent) => void;
};

type TablePlugins = {
  showRowIndex?: boolean;
  addRowCheckbox?: boolean;
  enableAvgMenu?: boolean;
};

export type TableV2Options = Omit<Options, "footerElement"> & TablePlugins;

type TrendratingTableV2Props = {
  config?: Omit<TableParamsV2, "node">;
};

type Rows = RowComponent[];

export type TableParamsV2 = {
  node: any;
  events?: TableEventsV2;
  tableOption?: TableV2Options;
};

type HeaderMenu = {
  label: string;
  action: (e: any, column: ColumnComponent) => void;
}[];

export const useTrendratingTable = (config?: Omit<TableParamsV2, "node">) => {
  const [tableInstance, setTableInstance] = useState<TableV2>();
  const [rendersCounter, setRenderCounters] = useState(0);

  const init = useCallback(
    (node: any) => {
      if (node) {
        setTableInstance(new TableV2({ node, ...config }));
        setRenderCounters((current) => current++);
      }
    },
    [config]
  );

  useEffect(() => {
    if (rendersCounter > 1) {
      console.warn(
        `The table has rendered ${rendersCounter} times. Is this the right behaviour?`
      );
    }
  }, [rendersCounter]);

  return { init, tableInstance };
};

export class TableV2 {
  private table: TabulatorFull;
  private menuActionBroadcast?: (
    e: any,
    column: ColumnComponent,
    actionTag: string
  ) => void;

  constructor({ node, events, tableOption }: TableParamsV2) {
    if (tableOption && "columns" in tableOption) {
      tableOption.columns = tableOption.columns as ColumnDefinition[];
    }

    this.table = new TabulatorFull(node, tableOption);

    if (events) {
      this.registerEvents(events);
    }
  }

  public insertColumns(columns: ColumnDefinition[]) {
    this.table.setColumns(columns);
  }

  public getFields() {
    const fields: string[] = [];
    let field = "";

    for (const column of this.table.getColumns()) {
      field = column.getField();

      if (field && field !== "idx") {
        fields.push(field);
      }
    }

    return fields;
  }

  public registerEvents(events: TableEventsV2) {
    this.handleEventSubscriptions(events, true);
  }

  public removeEvents(events: TableEventsV2) {
    this.handleEventSubscriptions(events, false);
  }

  public insertData(data: any, params?: any, config?: any) {
    if ((this.table.options as TableParamsV2["tableOption"])?.showRowIndex) {
      this.addRowIdx();
    }

    if ((this.table.options as TableParamsV2["tableOption"])?.addRowCheckbox) {
      this.addRowCheckbox();
    }

    return this.table.setData(data, params, config);
  }

  public redraw(force?: boolean) {
    this.table.redraw(force);
  }

  public destroy() {
    this.table.destroy();
  }

  public getRows(): Rows {
    return this.table.getRows();
  }

  public getSelected(): Rows {
    return this.table.getSelectedRows();
  }

  public addMenu(column: string | ColumnDefinition, menuItems: HeaderMenu) {
    if (typeof column === "string") {
      const definitions = this.table.getColumnDefinitions();

      for (const col of definitions) {
        if (col.field === column) {
          const menu: any = {
            headerMenu: menuItems,
          };
          this.table.updateColumnDefinition(col.field, menu);

          break;
        }
      }
    } else {
      column["headerMenu"] = menuItems;
    }
  }

  public getColumns() {
    return this.table.getColumns();
  }

  public addColumns(columns: ColumnDefinition[]) {
    const columnsDefinitions = columns;

    for (const definition of columnsDefinitions) {
      this.table.addColumn(definition);
    }
  }

  public removeColumns(columns: any) {
    for (const col of columns) {
      this.table.deleteColumn(col.field);
    }
  }

  private handleEventSubscriptions(events: TableEventsV2, subscribe: boolean) {
    for (const eventKey in events) {
      switch (eventKey) {
        case "headerSort": {
          // Tabulator don't provide an header sort event
          // This is a Trendrating logic, our tables sort on headerClick events
          this.table.off("headerClick");
          if (subscribe) {
            this.table.on("headerClick", (tabulatorEvent, column) =>
              this.wrapHeaderClickIntoHeaderSort(
                tabulatorEvent,
                column,
                events[eventKey] as any
              )
            );
          }

          break;
        }
        case "rowSelectionChanged":
        case "dataLoaded":
        case "rowSelected":
        case "rowDeselected":
        case "rowClick": {
          this.table.off(eventKey);
          if (subscribe) {
            this.table.on(eventKey, events[eventKey] as any);
          }

          break;
        }

        case "onTableReady": {
          this.table.off("tableBuilt");

          if (subscribe) {
            this.table.on("tableBuilt", events[eventKey]);
          }

          break;
        }

        case "menuHeaderClick": {
          if (subscribe) {
            this.menuActionBroadcast = events[eventKey];
          } else {
            this.menuActionBroadcast = undefined;
          }

          break;
        }

        case "onRowDbClick": {
          this.table.off("rowDblClick");

          if (subscribe) {
            this.table.on("rowDblClick", events[eventKey]);
          }

          break;
        }

        default: {
          console.warn(
            `The event: ${eventKey} was not registered because is not supported by the TableV2 class`
          );
        }
      }
    }
  }

  private wrapHeaderClickIntoHeaderSort(
    event: any,
    column: ColumnComponent,
    callback: (value: { field: string; direction: string }) => void
  ) {
    const sorters = column.getTable().getSorters();

    let actualDirection =
      column.getDefinition().headerSortStartingDir ?? "desc";
    if (sorters.length > 0 && sorters[0].field === column.getField()) {
      actualDirection = sorters[0].dir === "asc" ? "desc" : "asc";
    }

    const colField = column.getField();

    if (colField && colField !== "idx") {
      callback({ direction: actualDirection, field: colField });
    }
  }

  private addRowIdx() {
    const currentCols = this.table.getColumns();
    const exists = currentCols.some((col) => col.getField() === "idx");

    if (!exists) {
      this.table.addColumn(
        {
          title: "",
          field: "idx",
          formatter: "rownum",
          headerSort: false,
        },
        true,
        currentCols[0].getField()
      );
    }
  }

  private addRowCheckbox() {
    const currentCols = this.table.getColumns();
    const exists = currentCols.some((col) => {
      const def = col.getDefinition();

      return def.formatter === "rowSelection";
    });

    if (!exists) {
      this.table.addColumn(
        {
          title: "",
          formatter: "rowSelection",
          widthGrow: 0.5,
          hozAlign: "center",
          headerHozAlign: "center",
          headerSort: false,
        },
        true,
        currentCols[0].getField()
      );
    }
  }
}

export const TrendratingTableV2 = memo(
  forwardRef(({ config }: TrendratingTableV2Props, ref) => {
    const { init, tableInstance } = useTrendratingTable(config);

    useImperativeHandle(
      ref,
      () => ({
        table: tableInstance,
      }),
      [tableInstance]
    );

    useEffect(() => {
      return () => {
        tableInstance?.destroy();
      };
    });

    return <div style={{ width: "100%" }} ref={init}></div>;
  })
);
